import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

import Header from "../../components/layout/Header/Header";
import Footer from "../../components/layout/Footer/Footer";
import Container from "../../components/layout/Container/Container";

import s from "./TeamMemberPage.module.scss";

import { useEmployee } from "../../api/queries/useEmployee";
import { useGallery } from "../../api/queries/useGallery";
import { useServices } from "../../api/queries/useServices";
import { useEmployeeWorks } from "../../api/queries/useEmployeeWorks";

import type { components } from "../../api/schema";
import type { EmployeeWork } from "../../api/employeeWorks";

type Employee = components["schemas"]["Employee"];
type Gallery = components["schemas"]["Gallery"];
type Service = components["schemas"]["Service"];

type ViewerItem = {
  src: string;
  alt: string;
  title?: string;
};

function safeImgSrc(path?: string) {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  if (path.startsWith("/")) return path;
  return `/${path}`;
}

function phoneToHref(phone?: string) {
  const raw = (phone || "").trim();
  if (!raw) return "";

  const normalized = raw.replace(/[^\d+]/g, "");
  return normalized ? `tel:${normalized}` : "";
}

function fullName(e?: Employee | null) {
  if (!e) return "Сотрудник";

  const parts = [e.surname, e.name, (e as any).patronymic].filter(Boolean);
  return parts.join(" ").trim() || "Сотрудник";
}

function getAvatarId(e?: Employee | null): number | undefined {
  if (!e) return undefined;

  const anyE = e as any;
  const v = anyE.avatar_path_id ?? anyE.image_path_id;

  return typeof v === "number" && Number.isFinite(v) && v > 0 ? v : undefined;
}

function getIdArray(
  e: Employee | null | undefined,
  key: "sertificate_path_id" | "work_path_id"
): number[] {
  if (!e) return [];

  const v = (e as any)[key];
  if (!Array.isArray(v)) return [];

  return v.filter((x) => typeof x === "number" && Number.isFinite(x) && x > 0);
}

function splitMetaLines(...parts: Array<string | undefined>) {
  const raw = parts.filter(Boolean).join("\n");

  return raw
    .split(/\n|;|,/g)
    .map((x) => x.trim())
    .filter(Boolean)
    .slice(0, 5);
}

function EmployeePhoto({
  photoId,
  name,
  galleryMap,
}: {
  photoId?: number;
  name: string;
  galleryMap: Map<number, Gallery>;
}) {
  const g = photoId ? galleryMap.get(photoId) : undefined;
  const src = safeImgSrc(g?.image_path);

  if (!src) {
    return (
      <div className={s.photoFallback} aria-hidden="true">
        {name.slice(0, 1).toUpperCase()}
      </div>
    );
  }

  return <img className={s.photoImg} src={src} alt={name} loading="lazy" />;
}

function GalleryStrip({
  title,
  ids,
  galleryMap,
  onOpenViewer,
}: {
  title: string;
  ids: number[];
  galleryMap: Map<number, Gallery>;
  onOpenViewer: (items: ViewerItem[], index: number) => void;
}) {
  const items = ids.map((id) => galleryMap.get(id)).filter(Boolean) as Gallery[];

  if (items.length === 0) return null;

  const viewerItems: ViewerItem[] = items
    .map((g) => {
      const src = safeImgSrc(g.image_path);
      if (!src) return null;

      return {
        src,
        alt: g.title || "Изображение",
        title: g.title || undefined,
      };
    })
    .filter(Boolean) as ViewerItem[];

  return (
    <section className={s.sectionBlock}>
      <h2 className={s.sectionTitle}>{title}</h2>

      <div className={s.thumbGrid}>
        {viewerItems.map((item, index) => (
          <button
            key={`${item.src}_${index}`}
            type="button"
            className={s.thumbButton}
            onClick={() => onOpenViewer(viewerItems, index)}
          >
            <div className={s.thumb}>
              <img className={s.thumbImg} src={item.src} alt={item.alt} loading="lazy" />
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}

function WorksSection({
  works,
  galleryMap,
  onOpenViewer,
}: {
  works: EmployeeWork[];
  galleryMap: Map<number, Gallery>;
  onOpenViewer: (items: ViewerItem[], index: number) => void;
}) {
  if (!works.length) return null;

  return (
    <section className={s.sectionBlock}>
      <h2 className={s.sectionTitle}>Выполненные работы</h2>

      <div className={s.worksList}>
        {works.map((work) => {
          const photos = (Array.isArray(work.photo_ids) ? work.photo_ids : [])
            .map((id) => galleryMap.get(id))
            .filter(Boolean) as Gallery[];

          const viewerItems: ViewerItem[] = photos
            .map((g) => {
              const src = safeImgSrc(g.image_path);
              if (!src) return null;

              return {
                src,
                alt: g.title || work.title,
                title: g.title || work.title,
              };
            })
            .filter(Boolean) as ViewerItem[];

          return (
            <article key={work.id} className={s.workCard}>
              <h3 className={s.workTitle}>{work.title}</h3>

              <div className={s.caseGrid}>
                <div className={s.caseCard}>
                  <div className={s.caseTitle}>Проблема</div>
                  <div className={s.caseText}>{work.problem}</div>
                </div>

                <div className={s.caseCard}>
                  <div className={s.caseTitle}>Решение</div>
                  <div className={s.caseText}>{work.solution}</div>
                </div>
              </div>

              {viewerItems.length > 0 && (
                <div className={s.workGallery}>
                  {viewerItems.map((item, index) => (
                    <button
                      key={`${item.src}_${index}`}
                      type="button"
                      className={s.workThumbButton}
                      onClick={() => onOpenViewer(viewerItems, index)}
                    >
                      <div className={s.workThumb}>
                        <img
                          className={s.workThumbImg}
                          src={item.src}
                          alt={item.alt}
                          loading="lazy"
                        />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
}

export default function TeamMemberPage() {
  const params = useParams();
  const employeeId = Number(params.id);

  const { data: employee, isLoading, isError } = useEmployee(employeeId);
  const { data: gallery } = useGallery();
  const { data: services } = useServices();
  const { data: employeeWorks, isLoading: worksLoading } = useEmployeeWorks(employeeId);

  const [viewerItems, setViewerItems] = useState<ViewerItem[]>([]);
  const [viewerIndex, setViewerIndex] = useState(0);

  const safeGallery = Array.isArray(gallery) ? gallery : [];
  const safeServices = Array.isArray(services) ? services : [];
  const safeEmployeeWorks = Array.isArray(employeeWorks) ? employeeWorks : [];

  const galleryMap = useMemo(() => {
    const m = new Map<number, Gallery>();
    safeGallery.forEach((g: Gallery) => {
      if (typeof g.id === "number") m.set(g.id, g);
    });
    return m;
  }, [safeGallery]);

  const serviceMap = useMemo(() => {
    const m = new Map<number, Service>();
    safeServices.forEach((x: Service) => {
      if (typeof x.id === "number") m.set(x.id, x);
    });
    return m;
  }, [safeServices]);

  const employeeName = fullName(employee);
  const photoId = getAvatarId(employee);

  const position = (employee as any)?.position_at_work as string | undefined;
  const desc = ((employee as any)?.description as string | undefined)?.trim();
  const phone = ((employee as any)?.phone as string | undefined)?.trim();
  const phoneHref = phoneToHref(phone);

  const lines = splitMetaLines(position);

  const sid = (employee as any)?.services_id as number | undefined;
  const serviceTitle =
    typeof sid === "number" ? serviceMap.get(sid)?.title : undefined;
  const serviceLink = typeof sid === "number" ? `/services/${sid}` : undefined;

  const certificatesIds = getIdArray(employee, "sertificate_path_id");
  const legacyWorksIds = getIdArray(employee, "work_path_id");

  const showLegacyWorks = safeEmployeeWorks.length === 0 && legacyWorksIds.length > 0;

  const sortedWorks = useMemo(() => {
    return [...safeEmployeeWorks].sort((a, b) => {
      const byOrder = (a.sort_order ?? 0) - (b.sort_order ?? 0);
      if (byOrder !== 0) return byOrder;
      return (b.id ?? 0) - (a.id ?? 0);
    });
  }, [safeEmployeeWorks]);

  const viewerOpen = viewerItems.length > 0;
  const currentViewerItem = viewerItems[viewerIndex];

  const openViewer = (items: ViewerItem[], index: number) => {
    setViewerItems(items);
    setViewerIndex(index);
    document.body.style.overflow = "hidden";
  };

  const closeViewer = () => {
    setViewerItems([]);
    setViewerIndex(0);
    document.body.style.overflow = "";
  };

  const prevViewer = () => {
    setViewerIndex((prev) => (prev <= 0 ? viewerItems.length - 1 : prev - 1));
  };

  const nextViewer = () => {
    setViewerIndex((prev) => (prev >= viewerItems.length - 1 ? 0 : prev + 1));
  };

  useEffect(() => {
    if (!viewerOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeViewer();
      if (e.key === "ArrowLeft") prevViewer();
      if (e.key === "ArrowRight") nextViewer();
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewerOpen, viewerItems.length]);

  return (
    <div className={s.pageWrap}>
      <Header />

      <main className={s.main}>
        <section className="section section--tight">
          <Container className={s.wide}>
            <div className={s.panel}>
              <Link to="/team" className={s.backLink}>
                ← Назад к команде
              </Link>

              {isLoading ? (
                <div className={s.state}>Загрузка профиля специалиста…</div>
              ) : isError || !employee ? (
                <div className={s.state}>Не удалось загрузить страницу специалиста.</div>
              ) : (
                <>
                  <section className={s.hero}>
                    <div className={s.heroPhoto}>
                      <EmployeePhoto
                        photoId={photoId}
                        name={employeeName}
                        galleryMap={galleryMap}
                      />
                    </div>

                    <div className={s.heroText}>
                      <h1 className={s.h1}>{employeeName}</h1>

                      {lines.length > 0 && (
                        <div className={s.heroMeta}>
                          {lines.map((line) => (
                            <div key={line} className={s.heroMetaLine}>
                              {line}
                            </div>
                          ))}
                        </div>
                      )}

                      {phone && (
                        <div className={s.serviceBox}>
                          <span className={s.muted}>Телефон:</span>{" "}
                          {phoneHref ? (
                            <a className={s.serviceLink} href={phoneHref}>
                              {phone}
                            </a>
                          ) : (
                            <span>{phone}</span>
                          )}
                        </div>
                      )}

                      {serviceLink && (
                        <div className={s.serviceBox}>
                          <span className={s.muted}>Услуги:</span>{" "}
                          <Link className={s.serviceLink} to={serviceLink}>
                            {serviceTitle || "Открыть"}
                          </Link>
                        </div>
                      )}

                      {desc && (
                        <div className={s.descBox}>
                          <div className={s.sectionMiniTitle}>О специалисте</div>
                          <div className={s.descText}>{desc}</div>
                        </div>
                      )}
                    </div>
                  </section>

                  <GalleryStrip
                    title="Сертификаты"
                    ids={certificatesIds}
                    galleryMap={galleryMap}
                    onOpenViewer={openViewer}
                  />

                  {worksLoading ? (
                    <section className={s.sectionBlock}>
                      <h2 className={s.sectionTitle}>Выполненные работы</h2>
                      <div className={s.state}>Загрузка кейсов…</div>
                    </section>
                  ) : sortedWorks.length > 0 ? (
                    <WorksSection
                      works={sortedWorks}
                      galleryMap={galleryMap}
                      onOpenViewer={openViewer}
                    />
                  ) : showLegacyWorks ? (
                    <GalleryStrip
                      title="Выполненные работы"
                      ids={legacyWorksIds}
                      galleryMap={galleryMap}
                      onOpenViewer={openViewer}
                    />
                  ) : (
                    <section className={s.sectionBlock}>
                      <h2 className={s.sectionTitle}>Выполненные работы</h2>
                      <div className={s.state}>Пока выполненные работы не добавлены.</div>
                    </section>
                  )}
                </>
              )}
            </div>
          </Container>
        </section>
      </main>

      <Footer />

      {viewerOpen && currentViewerItem ? (
        <div className={s.viewerOverlay} role="dialog" aria-modal="true">
          <div className={s.viewerBackdrop} onClick={closeViewer} />

          <div className={s.viewerContent}>
            <button
              type="button"
              className={s.viewerClose}
              onClick={closeViewer}
              aria-label="Закрыть"
            >
              ×
            </button>

            {viewerItems.length > 1 ? (
              <>
                <button
                  type="button"
                  className={`${s.viewerNav} ${s.viewerPrev}`}
                  onClick={prevViewer}
                  aria-label="Предыдущее изображение"
                >
                  ‹
                </button>

                <button
                  type="button"
                  className={`${s.viewerNav} ${s.viewerNext}`}
                  onClick={nextViewer}
                  aria-label="Следующее изображение"
                >
                  ›
                </button>
              </>
            ) : null}

            <div className={s.viewerImageWrap}>
              <img
                src={currentViewerItem.src}
                alt={currentViewerItem.alt}
                className={s.viewerImage}
              />
            </div>

            {currentViewerItem.title ? (
              <div className={s.viewerCaption}>{currentViewerItem.title}</div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
